import Card from './ui/Card'
import Icon from './ui/Icon'
import { useNavigate } from 'react-router-dom'

const Leaderboard = ({ title, data, type = 'points' }) => {
  const navigate = useNavigate()

  const truncateName = (name, maxLength = 15) => {
    if (!name) return ''
    if (name.length > maxLength) {
      return name.substring(0, maxLength) + '...'
    }
    return name
  }

  const getRankIcon = (rank) => {
    switch (rank) {
      case 1:
        return '🥇'
      case 2:
        return '🥈'
      case 3:
        return '🥉'
      default:
        return rank
    }
  }

  const getMetricLabel = (type) => {
    return type === 'points' ? 'পয়েন্ট' : 'জাগিয়েছেন'
  }

  const getMetricValue = (item, type) => {
    const value = type === 'points' ? item.points : item.wokenUp
    return value != null ? Number(value) : 0
  }

  const formatStats = (item) => {
    const stats = []
    if (item.wakeup_count !== undefined) stats.push(`🌅 ${item.wakeup_count}`)
    if (item.total_helped !== undefined) stats.push(`🤝 ${item.total_helped}`)
    if (item.total_calls !== undefined) stats.push(`📞 ${item.total_calls}`)
    if (item.points !== undefined) stats.push(`${item.points}`)
    return stats
  }

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-bold text-foreground">{title}</h3>
        <Icon name="Trophy" className="w-6 h-6 text-secondary" />
      </div>
      
      <div className="space-y-2">
        {data.map((item) => (
          <div 
            key={item.rank}
            className={`relative overflow-hidden rounded-lg border transition-all duration-200 hover:shadow-md ${
              item.rank === 1 
                ? 'bg-gradient-to-r from-yellow-100 via-yellow-50 to-amber-100 border-yellow-300 shadow-lg' 
                : item.rank === 2
                ? 'bg-gradient-to-r from-gray-100 via-gray-50 to-slate-100 border-gray-300 shadow-md'
                : item.rank === 3
                ? 'bg-gradient-to-r from-orange-100 via-orange-50 to-amber-100 border-orange-300 shadow-md'
                : 'bg-white border-gray-200 hover:bg-gray-50'
            }`}
          >
            {/* Crown shadow overlay for 1st place */}
            {item.rank === 1 && (
              <div className="absolute inset-0 bg-gradient-to-br from-yellow-200/20 via-transparent to-amber-200/20 pointer-events-none" />
            )}
            
            <div className="p-3 relative">
              {/* Background rank badge shadow for top 3 */}
              {item.rank <= 3 && (
                <div className={`absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-6xl opacity-20 pointer-events-none select-none z-0 ${
                  item.rank === 1 ? 'text-yellow-600' : 
                  item.rank === 2 ? 'text-gray-600' : 
                  'text-orange-600'
                }`}>
                  {getRankIcon(item.rank)}
                </div>
              )}
              
              {/* Desktop Layout */}
              <div className="hidden sm:flex items-center justify-between relative z-10">
                {/* Name + Location */}
                <div className="flex items-center flex-1">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm text-gray-800 truncate">
                      {item.name}
                    </div>
                    {item.location && (
                      <div className="text-xs text-gray-500 flex items-center">
                        <Icon name="MapPin" className="w-3 h-3 mr-1 flex-shrink-0" />
                        <span className="truncate">{item.location}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* All Stats in One Row */}
                <div className="flex items-center space-x-3 text-xs">
                  <div className="text-green-600 font-medium flex flex-col items-center">
                    <span>{item.wakeup_count || 0}</span>
                    <span>rise</span>
                  </div>
                  <div className="text-blue-600 font-medium flex flex-col items-center">
                    <span>{item.total_helped || 0}</span>
                    <span>help</span>
                  </div>
                  <div className="text-purple-600 font-medium flex flex-col items-center">
                    <span>{item.total_calls || 0}</span>
                    <span>call</span>
                  </div>
                  <div className="text-orange-600 font-medium flex flex-col items-center">
                    <span className="text-3xl font-bold text-yellow-400">
                      {getMetricValue(item, type)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Mobile Layout */}
              <div className="sm:hidden relative z-10 space-y-1">
                {/* First Row: Name + Country */}
                <div className="flex items-center justify-between">
                  <div className="font-semibold text-sm text-gray-800">
                    {truncateName(item.name, 15)}
                  </div>
                  {item.location && (
                    <div className="text-xs text-gray-500">
                      {item.location}
                    </div>
                  )}
                </div>

                {/* Second Row: Stats */}
                <div className="text-xs text-gray-600 flex items-center gap-3">
                  <span>Rise: <strong className="text-green-600">{item.wakeup_count || 0}</strong></span>
                  <span>Help: <strong className="text-blue-600">{item.total_helped || 0}</strong></span>
                  <span>Call: <strong className="text-purple-600">{item.total_calls || 0}</strong></span>
                  <span>Point: <strong className="text-orange-600">{getMetricValue(item, type)}</strong></span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      
      <div className="mt-6 pt-4 border-t border-border">
        <div 
          className="text-center text-sm text-muted-foreground hover:text-primary cursor-pointer transition-colors"
          onClick={() => navigate('/leaderboard')}
        >
          আরো দেখুন →
        </div>
      </div>
    </Card>
  )
}

export default Leaderboard